"use client";
import { useState, useEffect, useRef } from "react";
import { Send, ThumbsUp, ThumbsDown, X, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
import { buildHeaders } from "../../api/buildHeaders";
import { useTranslation } from "react-i18next";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default function HalperChatView() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const [userStatus, setUserStatus] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const chatRef = useRef(null);
  const isPrependingRef = useRef(false);
  const {t} = useTranslation();
  const navigate = useNavigate();

  const sanitizeUrl = (url) => {
    if (!url) return null;
    let clean = url;
    if (clean.startsWith("http://") && !clean.includes("localhost") && !clean.includes("127.0.0.1")) {
      clean = clean.replace("http://", "https://");
    }
    if (clean.startsWith("/")) {
      const base = REST_API_URL.replace(/\/api\/?$/, "");
      clean = `${base}${clean}`;
    }
    return clean;
  };

  const formatHalperMessages = (items) => {
    const sorted = [...items].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
    return sorted.flatMap((msg) => [
      {
        id: msg.id,
        subId: `${msg.id}_user`,
        from: "user",
        text: msg.text,
        created_at: msg.created_at,
      },
      {
        id: msg.id,
        subId: `${msg.id}_halper`,
        from: "halper",
        text: msg.answer,
        reaction: msg.reaction,
        created_at: msg.created_at,
      },
    ]);
  };

  useEffect(() => {
    if (isPrependingRef.current) return;
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    loadMessages();
    fetchPaymentAndUser();
  }, []);

  const fetchPaymentAndUser = async () => {
    try {
      const headers = buildHeaders();

      const [userRes] = await Promise.all([
        fetch(`${REST_API_URL}/auth/userinfo/`, { headers }),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserStatus(userData?.user?.status ?? null);
      }
    } catch (e) {
      console.error("❌ Failed to fetch payment/user info:", e);
    }
  };

  const loadMessages = async () => {
    try {
      setInitialLoading(true);
      isPrependingRef.current = false;
      const headers = buildHeaders();

      const res = await fetch(`${API_BASE}/messaging/halper-messages-list/`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const rawResults = Array.isArray(data) ? data : (data?.results ?? []);
      const formatted = formatHalperMessages(rawResults);

      setMessages(formatted);
      const nextUrl = sanitizeUrl(data?.next);
      setNextPageUrl(nextUrl);
      setHasMore(Boolean(nextUrl));

      localStorage.setItem("halper_chat_messages", JSON.stringify(formatted));

      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      console.error("❌ Failed to load messages:", err);
      const cached = localStorage.getItem("halper_chat_messages");
      if (cached) setMessages(JSON.parse(cached));
      setNextPageUrl(null);
      setHasMore(false);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadOlderMessages = async () => {
    if (!nextPageUrl || loadingOlder || initialLoading) return;

    const chat = chatRef.current;
    if (!chat) return;

    const prevScrollHeight = chat.scrollHeight;
    const prevScrollTop = chat.scrollTop;

    try {
      setLoadingOlder(true);
      const headers = buildHeaders();

      const res = await fetch(nextPageUrl, {
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const rawResults = Array.isArray(data) ? data : (data?.results ?? []);
      const formattedOlder = formatHalperMessages(rawResults);

      const nextUrl = sanitizeUrl(data?.next);
      setNextPageUrl(nextUrl);
      setHasMore(Boolean(nextUrl));

      if (formattedOlder.length > 0) {
        isPrependingRef.current = true;
        setMessages((prev) => {
          const existingIds = new Set(
            prev.map((m) => m.subId || `${m.id}_${m.from}`)
          );
          const newOlder = formattedOlder.filter(
            (m) => !existingIds.has(m.subId || `${m.id}_${m.from}`)
          );
          return [...newOlder, ...prev];
        });

        requestAnimationFrame(() => {
          if (chatRef.current) {
            const newScrollHeight = chatRef.current.scrollHeight;
            chatRef.current.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
          setTimeout(() => {
            isPrependingRef.current = false;
          }, 150);
        });
      }
    } catch (err) {
      console.error("❌ Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = () => {
    const chat = chatRef.current;
    if (!chat) return;
    if (chat.scrollTop <= 60 && hasMore && !loadingOlder && !initialLoading && nextPageUrl) {
      loadOlderMessages();
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || loading) return;
    const textToSend = messageText.trim();

    if (!paymentCheck?.has_active_subscription) {
      setShowSubModal(true);
      return;
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", "halper_contact_support");
    }

    setMessages((prev) => [
      ...prev,
      { from: "user", text: textToSend, created_at: new Date().toISOString() },
    ]);
    setInputValue("");
    setLoading(true);

    const headers = buildHeaders();

    try {
      const response = await fetch(`${API_BASE}/messaging/halper-response/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: 637,
          text: textToSend,
          reaction: "neutral",
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setMessages((prev) => [
        ...prev,
        {
          id: data.id,
          from: "halper",
          text: data.answer || "Got it 👍",
          reaction: data.reaction,
          created_at: new Date().toISOString(),
        },
      ]);

      localStorage.setItem(
        "halper_chat_messages",
        JSON.stringify([
          ...messages,
          { text: textToSend },
          { text: data.answer },
        ]),
      );
    } catch (e) {
      console.error("❌ Error sending message:", e);
      setMessages((prev) => [
        ...prev,
        {
          from: "halper",
          text: t("home.halperChatViewError"),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendReaction = async (id, reactionType) => {
    try {
      const headers = buildHeaders();

      const res = await fetch(
        `${API_BASE}/messaging/halper-message-react/${id}/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ reaction: reactionType }),
        },
      );
      const data = await res.json();

      if (res.ok) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === id && msg.from === "halper"
              ? { ...msg, reaction: data.reaction }
              : msg,
          ),
        );
      } else {
        console.error("Reaction error:", data);
      }
    } catch (err) {
      console.error("❌ Failed to send reaction:", err);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const userTimeZone = getUserTimeZone();
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTimeZone,
      });
    } catch {
      return "";
    }
  };

  const isWorker = userStatus === "worker";

  return (
    <>
      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <X size={18} />
              </button>

              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>

              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                {isWorker ? t("home.halperChatViewSubRequired") : t("home.halperChatViewUpgradePlan")}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {isWorker
                  ? t("home.halperChatViewSubRequiredDescWorker")
                  : t("home.halperChatViewSubRequiredDesc")}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                >
                  {t("cancel")}
                </button>
                {!isWorker && (
                  <button
                    onClick={() => {
                      setShowSubModal(false);
                      navigate("/settings?tab=subscription");
                    }}
                    className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#020637] to-[#1e3aa0] text-white text-sm font-medium hover:opacity-90 transition"
                  >
                    {t("home.halperChatViewViewPlans")}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="relative bg-white rounded-[16px] w-[full] h-[993px] shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F6F6F6] text-[#020637] font-semibold text-lg">
              H
            </div>
            <span className="text-[#302B2B] font-medium text-[15px]">
              Halper {t("sidebar.chat")}
            </span>
          </div>
        </div>

        <div
          ref={chatRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-4"
        >
          {initialLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#020637]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-[#302B2B] opacity-70">
              <p className="text-[16px]">{t("home.halperChatViewStartConversation")}</p>
            </div>
          ) : (
            <>
              {loadingOlder && (
                <div className="flex justify-center items-center py-2 text-xs text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#020637] mr-2"></div>
                  {t("home.loadingOlderMessages", "Loading older messages...")}
                </div>
              )}
              {!loadingOlder && hasMore && (
                <div className="flex justify-center py-1">
                  <button
                    type="button"
                    onClick={loadOlderMessages}
                    className="text-xs text-[#020637] hover:underline bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition font-medium"
                  >
                    {t("home.loadOlderMessages", "Load older messages")}
                  </button>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} className="flex flex-col">
                  <div
                    className={`flex ${
                      msg.from === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${
                        msg.from === "user"
                          ? "bg-[#020637] text-white"
                          : "bg-[#F6F6F6] text-[#302B2B]"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>

                  <div
                    className={`text-xs text-gray-400 mt-1 ${msg.from === "user" ? "text-right mr-2" : "text-left ml-2"}`}
                  >
                    {formatTime(msg.created_at)}
                  </div>

                  {msg.from === "halper" && (
                    <div className="flex gap-3 mt-1 ml-2">
                      <button
                        onClick={() => sendReaction(msg.id, "like")}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#020637]"
                      >
                        <span
                          className={`p-1 rounded-full transition ${
                            msg.reaction === "like"
                              ? "bg-[#020637] text-white"
                              : "bg-transparent"
                          }`}
                        >
                          <ThumbsUp size={14} />
                        </span>
                      </button>

                      <button
                        onClick={() => sendReaction(msg.id, "dislike")}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#E1090C]"
                      >
                        <span
                          className={`p-1 rounded-full transition ${
                            msg.reaction === "dislike"
                              ? "bg-[#E1090C] text-white"
                              : "bg-transparent"
                          }`}
                        >
                          <ThumbsDown size={14} />
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#F6F6F6] text-[#302B2B] px-4 py-2 rounded-2xl text-sm opacity-70">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-[#020637] rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-[#020637] rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-2 h-2 bg-[#020637] rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 bg-[#F6F6F6] rounded-full px-4 py-2">
            <input
              type="text"
              placeholder={t("home.typeMessagePlaceholder")}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(inputValue)}
              className="flex-1 bg-transparent outline-none text-sm text-[#302B2B] placeholder-[#9A99AB]"
            />
            <button
              onClick={() => sendMessage(inputValue)}
              disabled={loading}
              className="bg-[#020637] hover:bg-[#060e4a] p-2 rounded-full text-white disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
